// Generation limit helpers: episode-count bounds and presigned-URL expiry
// resolution. Pure, layer-agnostic logic shared by the Agent Lambda (structure
// proposal / approval) and the Presigned-URL Lambda so both enforce the same
// bounds as a single source of truth.
//
// See design.md → Correctness Properties 10 (episode count) and 18 (presign
// expiry); Requirements 5.1, 5.3 (proposed/edited structures hold 3–12
// episodes) and 9.2 (media-URL validity in [60, 604800]s, defaulting to 3600).
/**
 * True when `value` is a finite integer. Non-finite (`NaN`, `Infinity`) and
 * fractional values are rejected so counts and durations are whole numbers.
 */
const isInteger = (value) => Number.isInteger(value);
// -- Episode count -----------------------------------------------------------
/** Inclusive lower bound on the number of episodes in a course. Req 5.1, 5.3. */
export const EPISODE_COUNT_MIN = 3;
/** Inclusive upper bound on the number of episodes in a course. Req 5.1, 5.3. */
export const EPISODE_COUNT_MAX = 12;
/**
 * Decide whether a proposed or edited course structure has an acceptable
 * episode count.
 *
 * Returns `true` if and only if `count` is an integer in [3, 12]. A course
 * structure is accepted as the basis for script generation exactly when this
 * returns `true` (Req 5.1, 5.3; Property 10). Non-integer, non-finite, or
 * out-of-range values return `false`.
 *
 * @param count - The number of episodes in the proposed/edited structure.
 * @returns `true` iff `count` is an integer within [{@link EPISODE_COUNT_MIN},
 *   {@link EPISODE_COUNT_MAX}].
 */
export function isValidEpisodeCount(count) {
    return isInteger(count) && count >= EPISODE_COUNT_MIN && count <= EPISODE_COUNT_MAX;
}
// -- Presigned-URL expiry ----------------------------------------------------
/** Inclusive lower bound on presigned-URL validity, in seconds. Req 9.2. */
export const PRESIGN_EXPIRY_MIN_SECONDS = 60;
/** Inclusive upper bound on presigned-URL validity, in seconds (7 days). Req 9.2. */
export const PRESIGN_EXPIRY_MAX_SECONDS = 604800;
/** Default presigned-URL validity applied when none is specified. Req 9.2. */
export const PRESIGN_EXPIRY_DEFAULT_SECONDS = 3600;
/**
 * Resolve the validity period (in seconds) for a presigned media URL.
 *
 * Resolution rules (Req 9.2; Property 18):
 *  - A requested value in [60, 604800] is accepted and used as-is.
 *  - An unspecified value (`undefined`) resolves to the default, 3600 seconds.
 *  - A requested value outside [60, 604800] — including non-integer or
 *    non-finite values — is rejected.
 *
 * @param expiresInSeconds - Requested validity in seconds, or `undefined` to
 *   use the default.
 * @returns `{ ok: true, seconds }` with the resolved validity when accepted, or
 *   `{ ok: false, message }` when the requested value is out of range.
 */
export function resolveExpiry(expiresInSeconds) {
    if (expiresInSeconds === undefined) {
        return { ok: true, seconds: PRESIGN_EXPIRY_DEFAULT_SECONDS };
    }
    if (!isInteger(expiresInSeconds) ||
        expiresInSeconds < PRESIGN_EXPIRY_MIN_SECONDS ||
        expiresInSeconds > PRESIGN_EXPIRY_MAX_SECONDS) {
        return {
            ok: false,
            message: `Presigned-URL expiry must be an integer number of seconds from ${PRESIGN_EXPIRY_MIN_SECONDS} to ${PRESIGN_EXPIRY_MAX_SECONDS}.`,
        };
    }
    return { ok: true, seconds: expiresInSeconds };
}
//# sourceMappingURL=generation-limits.js.map